import React, { useState, useEffect } from 'react';
import { IonContent, IonPage, IonRouterLink } from '@ionic/react';
import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useFetch } from 'use-http';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import InputField from 'common/atoms/inputField/InputField';
import useDebouncedValue from '../hooks/useDebouncedValue';
import Header from '../components/Header';
import Footer from '../components/Footer';

const defaultProfileImage = '/assets/profile/default-profile-image.svg';

const TeamList = () => {
  const [panels, setPanels] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showFooter, setShowFooter] = useState(true);
  const isNative = Capacitor.isNativePlatform();
  const {
    loading, error, get, abort,
  } = useFetch();

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const debouncedSearchText = useDebouncedValue(searchText, 1000);

  const dismissKeyboardOnEnter = (e) => {
    if (e.key === 'Enter' && isNative) {
      Keyboard.hide();
      e.target.blur();
    }
  };

  if (isNative) {
    Keyboard.addListener('keyboardWillShow', () => {
      setShowFooter(false);
    });

    Keyboard.addListener('keyboardDidHide', () => {
      setShowFooter(true);
    });
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams();
      params.set('sort', '["first_name","ASC"]');
      params.set('filter', `{"fullname":"${searchText}", "report":{"users":{"roles":["User"]}}}`);

      const data = await get(`/users?${params.toString()}`);
      if (cancelled) return;
      if (data) {
        setPanels(data);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedSearchText]);

  useEffect(() => () => {
    if (isNative) {
      Keyboard.removeAllListeners();
    }
  }, []);

  return (
    <IonPage>
      <Header />
      <IonContent fullscreen>
        <Typography variant="h4" sx={{ ml: 2 }}>Panel List</Typography>
        <InputField
          onChange={handleSearchTextChange}
          placeholder="Enter name"
          fieldValue={searchText}
          stylesOverride={{
            marginTop: '8px',
            marginLeft: '16px',
            marginRight: '16px',
          }}
          theme="light"
          enterKeyHint="done"
          onKeyUp={dismissKeyboardOnEnter}
        />
        {loading && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <CircularProgress size={60} />
          </Stack>
        )}
        {error && <Typography sx={{ ml: 2, mt: 2 }}>Failed to fetch panels</Typography>}
        {!loading && !error && (
          <List>
            {panels.map((panelOwner, index) => (
              <React.Fragment key={panelOwner.id}>
                <IonRouterLink routerLink={`/tabs/teams/admin/${panelOwner.id}`}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <img style={{ width: '3rem' }} src={defaultProfileImage} alt="profile" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        panelOwner?.first_name
                        && panelOwner?.last_name
                          ? `${panelOwner?.first_name} ${panelOwner?.last_name}'s Panel`
                          : `User ${panelOwner?.id}'s Panel`
                      }
                    />
                  </ListItem>
                </IonRouterLink>
                {panels[index + 1] ? <Divider /> : null}
              </React.Fragment>
            ))}
          </List>
        )}
      </IonContent>
      {showFooter && <Footer />}
    </IonPage>
  );
};

export default TeamList;
